import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, Observable} from "rxjs";
import {SettingsService} from "./settings.service";
import {SettingKey} from "../base/setting-key.enum";
import {filter} from "rxjs/operators";
import {SettingDto} from "../repositories/dto/setting.dto";
import {NavTree} from "../base/sidebar-body/nav.tree";
import {NavItem} from "../base/sidebar-body/nav.item";
import {PermissionGrant} from "./auth/permission-grant.enum";
import {PermissionService} from "./auth/permission.service";
import {isDefined} from "../libs/utils/epic.utils";
import {Feature} from "../libs/enum/feature.enum";

@Injectable({providedIn: 'root'})
export class NavService {

  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly permissionService: PermissionService = inject(PermissionService);

  private readonly sidebarMini = new BehaviorSubject<boolean | undefined>(undefined);
  private tree: NavTree | undefined;
  private prevNavItem: NavItem | undefined;
  private firstSteps: boolean = false;

  constructor() {
    this.settingsService.getSettingOnChange(SettingKey.USER_SIDEBAR_MINI)
      .pipe(filter((kv: SettingDto) => kv.value === 'true' || kv.value === 'false'))
      .subscribe((kv: SettingDto) => this.updateSidebarMini(kv.value === 'true'));
    this.settingsService.getOrgSettings().subscribe((ss) => {
      this.firstSteps = false;
      SettingsService.FIRST_STEPS_NAV.forEach((k) => {
        if (ss.find((s) => s.key === k)?.isFalse()) {
          this.firstSteps = true;
        }
      });
    });

    this.getSidebarMini()
      .pipe(filter((state) => isDefined(state)))
      .subscribe((state) => this.settingsService.updateUserSetting(SettingKey.USER_SIDEBAR_MINI, state ? 'true' : 'false'));
  }

  public getSidebarMini(): Observable<boolean | undefined> {
    return this.sidebarMini.asObservable().pipe(distinctUntilChanged());
  }

  public updateSidebarMini(state: boolean): void {
    this.sidebarMini.next(state);
  }

  public hasTree(): boolean {
    return isDefined(this.tree);
  }

  public setTree(tree: NavTree): void {
    const navTreeByPermission = this.filterNavItemsByPermission(tree.getItems());
    const navTreeByFeature = this.filterNavItemsByFeature(navTreeByPermission);
    const navTreeBySettings = this.filterNavItemsBySettings(navTreeByFeature);
    this.tree = new NavTree(navTreeBySettings);
  }

  public getTree(): NavTree {
    return this.tree!;
  }

  public select(item: NavItem | undefined): void {
    if (isDefined(item) && this.hasTree()) {
      this.prevNavItem = this.getTree().getSelected();
      this.getTree().select(item);
    }
  }

  public rollback(): void {
    if (this.prevNavItem && this.prevNavItem.id !== this.getTree().getSelected()?.id) {
      const rollback = this.prevNavItem;
      setTimeout(() => this.select(rollback), 10);
    }
  }

  /**
   * Attention: recursive function
   */
  private filterNavItemsByPermission(items: NavItem[]): NavItem[] {
    return items.filter((i) => this.hasPermission(i)).map((item) => {
      if (isDefined(item.children) && item.children.length > 0) {
        item.children = this.filterNavItemsByPermission(item.children);
      }
      return item;
    });
  }

  private hasPermission(item: NavItem): boolean {
    if (isDefined(item.permissions) && item.permissions.length > 0) {
      return item.permissions.every((scope) => this.permissionService.isAllow(scope, PermissionGrant.R));
    }
    // if no restriction is set, then access is allowed
    return true;
  }

  private filterNavItemsBySettings(items: NavItem[]): NavItem[] {
    if (this.firstSteps) {
      return items.filter((i: NavItem) => {
        return i.id !== 'invoice-main' && i.id !== 'org-wallet-transaction';
      });
    }
    return items;
  }

  private filterNavItemsByFeature(items: NavItem[]): NavItem[] {
    return items.filter((i: NavItem) => {
      if (isDefined(i.children) && i.children.length > 0) {
        i.children = this.filterNavItemsByFeature(i.children);
      }
      if (isDefined(i.features) && i.features.length > 0) {
        return i.features.every((f: Feature) => this.permissionService.isFeatureEnabled(f));
      }
      return true;
    });
  }
}
